import { format, parseISO } from 'date-fns'
import type {
  LiveEventAdmin,
  LiveEventGameMode,
  LiveEventSessionStatus,
} from 'api/generated'

/**
 * Host of the app's universal/app links (apple-app-site-association and
 * assetlinks.json in tennistalker-frontend). The QR must point here so that it
 * opens the app when installed.
 */
const LIVE_EVENT_FRONTEND_URL = 'https://www.tennistalker.it'

/** Same as the backend `LiveEvent::DEFAULT_THEME`: TennisTalker lime, deep olive court, iris targets. */
export const DEFAULT_THEME = {
  primary_color: '#d8ff53',
  secondary_color: '#191e01',
  accent_color: '#5b5bd6',
} as const

/** The static URL printed in the official QR: live (competitive) games. */
export const liveEventQrUrl = (
  event: Pick<LiveEventAdmin, 'slug' | 'live_key'>
) =>
  `${LIVE_EVENT_FRONTEND_URL}/evento/${event.slug}/live${
    event.live_key ? `?k=${encodeURIComponent(event.live_key)}` : ''
  }`

/** In-app entry point: practice games, secondary leaderboard. */
export const liveEventHomeUrl = (event: Pick<LiveEventAdmin, 'slug'>) =>
  `${LIVE_EVENT_FRONTEND_URL}/evento/${event.slug}`

/** Big screen page: open it logged in with a backoffice account. */
export const liveEventScreenUrl = (event: Pick<LiveEventAdmin, 'slug'>) =>
  `${LIVE_EVENT_FRONTEND_URL}/evento/${event.slug}/schermo`

export const MODE_LABELS: Record<LiveEventGameMode, string> = {
  live: 'Live (QR)',
  home: 'Da casa',
}

export const STATUS_LABELS: Record<LiveEventSessionStatus, string> = {
  queued: 'In coda',
  called: 'Chiamato',
  playing: 'In gioco',
  finished: 'Finita',
  expired: 'Scaduta',
  cancelled: 'Interrotta',
}

export const formatDateTime = (value: string | null | undefined) =>
  value ? format(parseISO(value), 'dd/MM/yyyy HH:mm') : '—'

export const formatDuration = (ms: number | null | undefined) =>
  ms == null ? '—' : `${Math.round(ms / 1000)}s`

/** `datetime-local` input value ↔ ISO string. */
export const toDateTimeLocal = (value: string | null | undefined) =>
  value ? format(parseISO(value), "yyyy-MM-dd'T'HH:mm") : ''

export const fromDateTimeLocal = (value: string) =>
  value ? new Date(value).toISOString() : null

export const copyToClipboard = (text: string) =>
  navigator.clipboard.writeText(text)
